'use client';

import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
	Search,
	Plus,
	Settings,
	ExternalLink,
	Calendar,
	Globe,
	Clock,
	Shield,
	Eye,
	EyeOff
} from 'lucide-react';
import Link from 'next/link';
import { Session } from 'next-auth';
import { LoadingSpinner } from '@/components/loading-indicator';

interface EmailDomain {
	id: string;
	domain: string;
	siteId: string;
	isPublic: boolean;
	isEnabled: boolean;
	awsManaged: boolean;
	awsStatus: 'requesting' | 'active' | 'external' | 'pending';
	isPending?: boolean;
	awsData?: any;
	awsOperation?: any;
	isDisabled?: boolean; // Mark disabled domains
}

function DomainList({ domains }: { domains: EmailDomain[] }) {
	// Group domains by their AWS status, not by enabled/disabled
	const requestingDomains = domains.filter((domain) => domain.awsStatus === 'requesting');
	const activeDomains = domains.filter((domain) => domain.awsStatus === 'active');
	const pendingDomains = domains.filter((domain) => domain.awsStatus === 'pending');
	const externalDomains = domains.filter((domain) => domain.awsStatus === 'external');

	const getStatusColor = (awsStatus: 'requesting' | 'active' | 'external' | 'pending') => {
		switch (awsStatus) {
			case 'active':
				return 'bg-green-100 text-green-800';
			case 'requesting':
				return 'bg-yellow-100 text-yellow-800';
			case 'pending':
				return 'bg-orange-100 text-orange-800';
			case 'external':
				return 'bg-blue-100 text-blue-800';
			default:
				return 'bg-gray-100 text-gray-800';
		}
	};

	const getStatusLabel = (awsStatus: 'requesting' | 'active' | 'external' | 'pending') => {
		switch (awsStatus) {
			case 'active':
				return 'Managed';
			case 'requesting':
				return 'Registering...';
			case 'pending':
				return 'Pending Setup';
			case 'external':
				return 'External Domain';
			default:
				return 'Unknown';
		}
	};

	const renderDomainCard = (domain: EmailDomain, sectionClass = '') => (
		<Card key={domain.id} className={`hover:shadow-md transition-shadow ${sectionClass}`}>
			<CardContent className="p-6">
				<div className="flex items-center justify-between">
					<div className="flex-1">
						<div className="flex items-center space-x-3">
							<h3 className="text-lg font-semibold text-gray-900">{domain.domain}</h3>
							<Badge
								className={`${getStatusColor(domain.awsStatus)} hover:${getStatusColor(domain.awsStatus)} cursor-default`}
							>
								{getStatusLabel(domain.awsStatus)}
							</Badge>
							{domain.isDisabled && (
								<Badge
									variant="outline"
									className="text-gray-600 hover:text-gray-600 cursor-default"
								>
									<EyeOff className="w-3 h-3 mr-1" />
									Disabled
								</Badge>
							)}
							{domain.awsData?.StatusList?.map((status: string, idx: number) => (
								<Badge
									key={idx}
									variant="outline"
									className="text-green-600 hover:text-green-600 cursor-default"
								>
									{status}
								</Badge>
							))}
						</div>
						<div className="flex items-center space-x-6 mt-2 text-sm text-gray-600">
							{domain.awsData?.Expiry && (
								<div className="flex items-center space-x-1">
									<Calendar className="w-4 h-4" />
									<span>
										Expires:{' '}
										{new Date(domain.awsData.Expiry).toLocaleDateString()}
									</span>
								</div>
							)}
							{domain.awsOperation?.submittedDate && (
								<div className="flex items-center space-x-1">
									<Calendar className="w-4 h-4" />
									<span>
										Requested:{' '}
										{new Date(
											domain.awsOperation.submittedDate
										).toLocaleDateString()}
									</span>
								</div>
							)}
							{domain.awsData?.AutoRenew && (
								<Badge
									variant="outline"
									className="text-green-600 hover:text-green-600 cursor-default"
								>
									Auto-renew enabled
								</Badge>
							)}
							{domain.awsData?.TransferLock && (
								<Badge
									variant="outline"
									className="text-blue-600 hover:text-blue-600 cursor-default"
								>
									Transfer locked
								</Badge>
							)}
							{domain.awsStatus === 'external' && (
								<span>
									This domain is managed externally and configured for email
									sending.
								</span>
							)}
							{domain.awsStatus === 'pending' && (
								<span className="text-orange-600">
									This domain is still being processed. DNS management will be
									available once setup is complete.
								</span>
							)}
							{domain.awsOperation?.operationId && (
								<span>Operation ID: {domain.awsOperation.operationId}</span>
							)}
							{domain.isDisabled && domain.awsStatus !== 'external' && (
								<span className="text-gray-500">
									This domain is disabled for email sending but can still be
									managed.
								</span>
							)}
						</div>
					</div>
					<div className="flex items-center space-x-2">
						{domain.awsStatus === 'requesting' && (
							<Button
								variant="outline"
								size="sm"
								onClick={() => window.location.reload()}
							>
								<Clock className="w-4 h-4 mr-1" />
								Refresh Status
							</Button>
						)}
						{domain.awsStatus === 'active' && (
							<Link href={`/domains/${domain.domain}/dns`}>
								<Button variant="outline" size="sm">
									<Settings className="w-4 h-4 mr-1" />
									Manage DNS
								</Button>
							</Link>
						)}
						{domain.awsStatus === 'pending' && (
							<>
								<Button
									variant="outline"
									size="sm"
									onClick={() => window.location.reload()}
								>
									<Clock className="w-4 h-4 mr-1" />
									Check Status
								</Button>
								<Button variant="outline" size="sm" disabled>
									<Settings className="w-4 h-4 mr-1" />
									DNS Setup Pending
								</Button>
							</>
						)}
						{domain.awsStatus === 'external' && (
							<Button variant="outline" size="sm" disabled>
								<Settings className="w-4 h-4 mr-1" />
								External Domain
							</Button>
						)}
					</div>
				</div>
			</CardContent>
		</Card>
	);

	return (
		<div className="space-y-6">
			{/* Requesting Domains Section */}
			{requestingDomains.length > 0 && (
				<div className="space-y-4">
					<div className="flex items-center space-x-2">
						<h3 className="text-lg font-semibold text-gray-900">
							Domain Requests ({requestingDomains.length})
						</h3>
						<Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 hover:text-yellow-800 cursor-default">
							In Progress
						</Badge>
					</div>
					{requestingDomains.map((domain) =>
						renderDomainCard(domain, 'border-yellow-200')
					)}
				</div>
			)}

			{/* Active AWS Domains Section */}
			{activeDomains.length > 0 && (
				<div className="space-y-4">
					<div className="flex items-center space-x-2">
						<h3 className="text-lg font-semibold text-gray-900">
							Managed Domains ({activeDomains.length})
						</h3>
						<Badge className="bg-green-100 text-green-800 hover:bg-green-100 hover:text-green-800 cursor-default">
							Active
						</Badge>
					</div>
					{activeDomains.map((domain) => renderDomainCard(domain))}
				</div>
			)}

			{/* Pending AWS Domains Section */}
			{pendingDomains.length > 0 && (
				<div className="space-y-4">
					<div className="flex items-center space-x-2">
						<h3 className="text-lg font-semibold text-gray-900">
							Pending Setup ({pendingDomains.length})
						</h3>
						<Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100 hover:text-orange-800 cursor-default">
							Pending
						</Badge>
					</div>
					{pendingDomains.map((domain) => renderDomainCard(domain, 'border-orange-200'))}
				</div>
			)}

			{/* External Domains Section */}
			{externalDomains.length > 0 && (
				<div className="space-y-4">
					<div className="flex items-center space-x-2">
						<h3 className="text-lg font-semibold text-gray-900">
							External Domains ({externalDomains.length})
						</h3>
						<Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 hover:text-blue-800 cursor-default">
							External
						</Badge>
					</div>
					{externalDomains.map((domain) => renderDomainCard(domain, 'border-blue-200'))}
				</div>
			)}
		</div>
	);
}

export default function DomainsPageClient({ session }: { session: Session | null }) {
	const [enabledDomains, setEnabledDomains] = useState<EmailDomain[]>([]);
	const [disabledDomains, setDisabledDomains] = useState<EmailDomain[]>([]);
	const [activeTab, setActiveTab] = useState<'active' | 'disabled' | 'all'>('active');
	const [searchQuery, setSearchQuery] = useState('');
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		fetchDomains();
	}, []);

	const fetchDomains = async () => {
		try {
			setLoading(true);
			const response = await fetch(`/api/sites/${session?.siteId}/domains`);
			const data = await response.json();

			if (data.enabledDomains && data.disabledDomains) {
				setEnabledDomains(data.enabledDomains || []);
				setDisabledDomains(data.disabledDomains || []);
			}
		} catch (error) {
			console.error('Error fetching domains:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleSearch = () => {
		if (searchQuery.trim()) {
			window.location.href = `/domains/search?q=${encodeURIComponent(searchQuery.trim())}`;
		}
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch();
		}
	};

	if (loading) {
		return (
			<div className="container mx-auto p-6">
				<div className="flex items-center justify-center h-64">
					<LoadingSpinner />
				</div>
			</div>
		);
	}

	return (
		<div className="container mx-auto p-6 space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-3xl font-bold text-gray-900">Domain Management</h1>
					<p className="text-gray-600 mt-1">
						Manage your domains, DNS settings, and hosting zones
					</p>
				</div>
				<div className="flex gap-2">
					<Link href="/domains/import">
						<Button variant="outline">
							<ExternalLink className="w-4 h-4 mr-2" />
							Import Domains
						</Button>
					</Link>
					<Link href="/domains/search">
						<Button className="bg-blue-600 hover:bg-blue-700">
							<Plus className="w-4 h-4 mr-2" />
							Register Domain
						</Button>
					</Link>
				</div>
			</div>

			{/* Search Bar */}
			<Card>
				<CardContent className="p-6">
					<div className="flex items-center space-x-4">
						<div className="flex-1 relative">
							<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
							<Input
								type="text"
								placeholder="Search for a domain name (e.g., example.com)"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								onKeyPress={handleKeyPress}
								className="pl-10"
							/>
						</div>
						<Button onClick={handleSearch} className="bg-blue-600 hover:bg-blue-700">
							Search
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* My Domains */}
			<div className="space-y-4">
				{enabledDomains.length === 0 && disabledDomains.length === 0 ? (
					<Card>
						<CardContent className="p-12 text-center">
							<Globe className="w-12 h-12 text-gray-400 mx-auto mb-4" />
							<h3 className="text-lg font-medium text-gray-900 mb-2">
								No domains yet
							</h3>
							<p className="text-gray-600 mb-4">
								Get started by registering your first domain
							</p>
							<Link href="/domains/search">
								<Button className="bg-blue-600 hover:bg-blue-700">
									<Plus className="w-4 h-4 mr-2" />
									Register Your First Domain
								</Button>
							</Link>
						</CardContent>
					</Card>
				) : (
					<Tabs
						value={activeTab}
						onValueChange={(value) =>
							setActiveTab(value as 'active' | 'disabled' | 'all')
						}
					>
						<TabsList className="grid w-full grid-cols-3">
							<TabsTrigger value="active" className="flex items-center space-x-2">
								<Eye className="w-4 h-4" />
								<span>Active ({enabledDomains.length})</span>
							</TabsTrigger>
							<TabsTrigger value="disabled" className="flex items-center space-x-2">
								<EyeOff className="w-4 h-4" />
								<span>Disabled ({disabledDomains.length})</span>
							</TabsTrigger>
							<TabsTrigger value="all" className="flex items-center space-x-2">
								<Globe className="w-4 h-4" />
								<span>All ({enabledDomains.length + disabledDomains.length})</span>
							</TabsTrigger>
						</TabsList>

						<TabsContent value="active" className="space-y-4">
							{enabledDomains.length === 0 ? (
								<Card>
									<CardContent className="p-8 text-center">
										<Eye className="w-8 h-8 text-gray-400 mx-auto mb-4" />
										<h3 className="text-lg font-medium text-gray-900 mb-2">
											No active domains
										</h3>
										<p className="text-gray-600">
											Enable domains from the disabled tab or register a new
											domain
										</p>
									</CardContent>
								</Card>
							) : (
								<DomainList domains={enabledDomains} />
							)}
						</TabsContent>

						<TabsContent value="disabled" className="space-y-4">
							{disabledDomains.length === 0 ? (
								<Card>
									<CardContent className="p-8 text-center">
										<EyeOff className="w-8 h-8 text-gray-400 mx-auto mb-4" />
										<h3 className="text-lg font-medium text-gray-900 mb-2">
											No disabled domains
										</h3>
										<p className="text-gray-600">
											All your domains are currently active
										</p>
									</CardContent>
								</Card>
							) : (
								<DomainList domains={disabledDomains} />
							)}
						</TabsContent>

						<TabsContent value="all" className="space-y-4">
							<DomainList domains={[...enabledDomains, ...disabledDomains]} />
						</TabsContent>
					</Tabs>
				)}
			</div>
		</div>
	);
}
